import config from '../../../config'
import { authHeader, customFetch, handleResponse, commonRequestOptions } from '../../../helpers'
import { appName } from '../appConfig'

const base = `${appName}/candidate`

export const candidateService = {
  getAll,
  getUsers,
  getByUuid,
  createBulk,
  update,
  remove,
  addMilestone,
  updateMilestone,
  removeMilestone,
  getInterviews,
  updateInterviewRound,
  removeInterviewRound,
  toggleInterviewSignup,
}

function getAll(signal) {
  const opts = { method: 'GET', headers: authHeader(), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}/all`, opts, signal).then(handleResponse)
}

function getUsers(signal) {
  const opts = { method: 'GET', headers: authHeader(), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}/users`, opts, signal).then(handleResponse)
}

function getByUuid(uuid, signal) {
  const opts = { method: 'GET', headers: authHeader(), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}/${uuid}`, opts, signal).then(handleResponse)
}

function createBulk(payload, signal) {
  const opts = {
    method:  'POST',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
    ...commonRequestOptions(),
  }
  return customFetch(`${config.api.url}/${base}/bulk`, opts, signal).then(handleResponse)
}

function update(uuid, payload, signal) {
  const opts = {
    method:  'PUT',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
    ...commonRequestOptions(),
  }
  return customFetch(`${config.api.url}/${base}/${uuid}`, opts, signal).then(handleResponse)
}

function remove(uuid, signal) {
  const opts = {
    method:  'DELETE',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body:    JSON.stringify({}),
    ...commonRequestOptions(),
  }
  return customFetch(`${config.api.url}/${base}/${uuid}`, opts, signal).then(handleResponse)
}

function addMilestone(uuid, payload, signal) {
  const opts = {
    method:  'POST',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
    ...commonRequestOptions(),
  }
  return customFetch(`${config.api.url}/${base}/${uuid}/milestones`, opts, signal).then(handleResponse)
}

function updateMilestone(uuid, milestoneId, payload, signal) {
  const opts = {
    method:  'PUT',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
    ...commonRequestOptions(),
  }
  return customFetch(`${config.api.url}/${base}/${uuid}/milestones/${milestoneId}`, opts, signal).then(handleResponse)
}

// Booked interviews. Mirrors the events app's calendar contract: pass {all:true} for
// everything, {start, end} for a calendar window (Date or ISO), or {daysAhead} for the
// upcoming list. No params = the API's default upcoming window.
function getInterviews(params = {}, signal) {
  const query = new URLSearchParams()
  if (params.all) query.set('all', 'true')
  if (params.start) query.set('start', new Date(params.start).toISOString())
  if (params.end) query.set('end', new Date(params.end).toISOString())
  if (params.daysAhead) query.set('daysAhead', String(params.daysAhead))
  const qs = query.toString()
  const opts = { method: 'GET', headers: authHeader(), ...commonRequestOptions() }
  return customFetch(`${config.api.url}/${base}/interviews${qs ? `?${qs}` : ''}`, opts, signal).then(handleResponse)
}

// Reschedule / edit an already-booked round. Booking a round for the first time happens
// through addMilestone — a booking only exists alongside the milestone that requires it.
function updateInterviewRound(uuid, round, payload, signal) {
  const opts = {
    method:  'PUT',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body:    JSON.stringify(payload),
    ...commonRequestOptions(),
  }
  return customFetch(`${config.api.url}/${base}/${uuid}/interviews/${round}`, opts, signal).then(handleResponse)
}

// Cancel a booking ("Avboka"). Attendees go with it; the milestone stays, since it records
// that the candidate reached this round.
function removeInterviewRound(uuid, round, signal) {
  const opts = {
    method:  'DELETE',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body:    JSON.stringify({}),
    ...commonRequestOptions(),
  }
  return customFetch(`${config.api.url}/${base}/${uuid}/interviews/${round}`, opts, signal).then(handleResponse)
}

// Toggle whether the logged-in user is attending. Returns
// {attendee_count, user_has_signed_up, attendees}.
function toggleInterviewSignup(interviewUuid, signal) {
  const opts = {
    method:  'POST',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body:    JSON.stringify({}),
    ...commonRequestOptions(),
  }
  return customFetch(`${config.api.url}/${base}/interviews/${interviewUuid}/signup`, opts, signal).then(handleResponse)
}

function removeMilestone(uuid, milestoneId, signal) {
  const opts = {
    method:  'DELETE',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body:    JSON.stringify({}),
    ...commonRequestOptions(),
  }
  return customFetch(`${config.api.url}/${base}/${uuid}/milestones/${milestoneId}`, opts, signal).then(handleResponse)
}
